/**
 * useFirstRunCallout — local hook owning the first-run callout's visibility.
 *
 * Plan: .plan/features/2026-06-14-firstrun-onboarding.md
 *
 * First-run = the empty-library component is mounted (so the library IS empty)
 * AND `localStorage["proof.firstRunCalloutDismissed"]` is unset. The empty-state
 * component only renders when the library is empty, so the "empty" half is
 * implied by the hook being called from inside it.
 *
 * SSR / first-paint policy: default to NOT-shown, then re-check localStorage in
 * an effect. This avoids a flash-then-hide for users who have already dismissed —
 * the callout only appears once the effect confirms the flag is unset. The
 * trade-off (a one-frame delay before the callout appears for a genuine
 * first-run user) is invisible in practice and strictly better than showing the
 * card to a user who dismissed it and then yanking it away.
 *
 * Storage failures fail OPEN is NOT the choice here — we fail CLOSED on read
 * errors (treat as "cannot confirm unset" → keep hidden) only if reading throws,
 * but reading localStorage practically never throws; writing can (private mode,
 * quota). The write is wrapped so dismiss never throws (Rule 8: the visible
 * effect — the card hiding — still happens via local state even if the flag
 * cannot persist).
 *
 * Storage key uses the mandated `proof.` prefix (CLAUDE.md §5 / tokens.css note).
 */

import { useCallback, useEffect, useState } from "react";

/** localStorage key — `proof.` prefix per CLAUDE.md storage-key rule. */
export const FIRST_RUN_CALLOUT_KEY = "proof.firstRunCalloutDismissed";

export interface FirstRunCalloutState {
  /** True when the first-run callout should render. */
  show: boolean;
  /** Persists the dismissed flag and hides the callout. Never throws. */
  dismiss: () => void;
}

/**
 * Returns whether the first-run callout should be shown, plus a dismiss action.
 *
 * Call this ONLY from inside the empty-library surface — the library being
 * empty is a precondition of this hook's "first run" meaning.
 */
export function useFirstRunCallout(): FirstRunCalloutState {
  // Default hidden; the effect flips it on once it confirms the flag is unset.
  const [show, setShow] = useState(false);

  useEffect(() => {
    let dismissed = true; // fail closed on read error → keep hidden
    try {
      dismissed = window.localStorage.getItem(FIRST_RUN_CALLOUT_KEY) !== null;
    } catch {
      // localStorage unavailable (e.g. blocked) — treat as dismissed so we
      // never show a callout we cannot let the user dismiss persistently.
      dismissed = true;
    }
    setShow(!dismissed);
  }, []);

  const dismiss = useCallback(() => {
    // Hide immediately via local state — the visible effect always happens,
    // even if persistence fails (Rule 8: the user sees their action take hold).
    setShow(false);
    try {
      window.localStorage.setItem(FIRST_RUN_CALLOUT_KEY, "1");
    } catch (err) {
      // Persistence failed (private mode / quota). The card is already hidden
      // for this session; it may reappear next visit. Log, do not throw —
      // a swallowed-silent failure would be a Rule 8 violation, a thrown error
      // would break an otherwise-harmless dismiss.
      // eslint-disable-next-line no-console
      console.warn("firstRunCallout: could not persist dismiss flag", err);
    }
  }, []);

  return { show, dismiss };
}
